import { Routes, Route, Navigate, useParams, useSearchParams, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';

import { NotFoundPage, ServiceUnavailablePage } from '@/modules/error-view';

import { ProfilePage } from '@/modules/profile';
import { MainLayout } from '@/layout/main-layout/main-layout';
import { FullPageLayout } from '@/layout/full-page-layout/full-page-layout';
import { AuthRoutes } from './auth.route';
import { Guard } from '@/state/store/auth/guard';
import { ClientMiddleware } from '@/state/client-middleware';
import { ThemeProvider } from '@/styles/theme/theme-provider';
import { SidebarProvider } from '@/components/ui-kit/sidebar';
import { Toaster } from '@/components/ui-kit/toaster';
import { useLanguageContext } from '@/i18n/language-context';
import { LoadingOverlay } from '@/components/core';
import { GptChatPage } from '@/modules/gpt-chats/pages/gpt-chat/gpt-chat';
import { GptChatPageDetails } from '@/modules/gpt-chats/pages/gpt-chat-details/gpt-chat-details';
import { ProtectedRoute } from '@/state/store/auth/protected-route';
import { UsersTablePage } from '@/modules/iam';
import { CreateNotePage, EditNotePage, NotesPage } from '@/modules/notes';
import { AIModels, AIModelSelectedPage } from '@/modules/gpt-chats/pages/aimodels';
import { KnowledgeBase, KnowledgeBaseFolder } from '@/modules/gpt-chats/pages/knowledge-base';
import { Tools } from '@/modules/gpt-chats/pages/tools';
import { Agents } from '@/modules/gpt-chats/pages/agents';
import { AgentDetailsPage } from '@/modules/gpt-chats/pages/agent-details';

import { ToolDetailsLayout } from '@/modules/gpt-chats/pages/tools/tool-api-details/tool-details-layout';
import { ToolAuthentication } from '@/modules/gpt-chats/pages/tools/tool-api-details/tool-authentication';
import { ToolAPI } from '@/modules/gpt-chats/pages/tools/tool-api-details/tool-api';
import { ToolDashboard } from '@/modules/gpt-chats/pages/tools/tool-api-details/tool-dashboard';
import { ToolTutorialGuide } from '@/modules/gpt-chats/pages/tools/tool-api-details/tool-tutorial-guide';
import { CreateAPIWrapper } from '@/modules/gpt-chats/pages/tools/tool-api-details/create-api/create-api';
import { McpServerDetailsLayout } from '@/modules/gpt-chats/pages/tools/mcp-server-details/mcp-server-details-layout';
import { McpServerConfiguration } from '@/modules/gpt-chats/pages/tools/mcp-server-details/mcp-server-configuration';

const AiRouteShell = ({ children }: { children: ReactNode }) => (
  <div className="h-full w-full overflow-auto p-4 sm:p-6 md:p-8">{children}</div>
);

const ModelProviderRoute = () => {
  const { provider = '' } = useParams();
  return <AIModelSelectedPage provider={provider} />;
};

const KnowledgeBaseFolderRoute = () => {
  const { folder = '' } = useParams();
  return <KnowledgeBaseFolder folderId={folder} />;
};

const ToolDetailsRoute = ({
  tab,
}: {
  tab: 'authentication' | 'api' | 'dashboard' | 'tutorial-guide';
}) => {
  const { id = '' } = useParams();
  const content = {
    authentication: <ToolAuthentication toolId={id} />,
    api: <ToolAPI toolId={id} />,
    dashboard: <ToolDashboard toolId={id} />,
    'tutorial-guide': <ToolTutorialGuide toolId={id} />,
  }[tab];

  return <ToolDetailsLayout toolId={id}>{content}</ToolDetailsLayout>;
};

const McpServerDetailsRoute = ({
  tab,
}: {
  tab: 'configuration' | 'dashboard' | 'tutorial-guide';
}) => {
  const { id = '' } = useParams();
  const content =
    tab === 'configuration' ? (
      <McpServerConfiguration mcpServerId={id} />
    ) : tab === 'dashboard' ? (
      <ToolDashboard toolId={id} />
    ) : (
      <ToolTutorialGuide toolId={id} />
    );

  return <McpServerDetailsLayout mcpServerId={id}>{content}</McpServerDetailsLayout>;
};

const CreateAPIRoute = () => {
  const { toolId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const step = searchParams.get('step');

  return <CreateAPIWrapper toolId={toolId} initialStep={step ? Number(step) : undefined} />;
};

export const AppRoutes = () => {
  const { isLoading } = useLanguageContext();

  if (isLoading) {
    return <LoadingOverlay />;
  }
  return (
    <div className="min-h-screen bg-background font-sans antialiased relative">
      <ClientMiddleware>
        <ThemeProvider>
          <SidebarProvider>
            <Routes>
              {AuthRoutes}
              <Route
                element={
                  <Guard>
                    <MainLayout />
                  </Guard>
                }
              >
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <UsersTablePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/profile" element={<ProfilePage />} />

                <Route path="/chat" element={<GptChatPage />} />
                <Route path="/chat/:chatId" element={<GptChatPageDetails />} />

                <Route element={<ProtectedRoute roles={['admin']}><Outlet /></ProtectedRoute>}>
                  <Route
                    path="/ai/models"
                    element={
                      <AiRouteShell>
                        <AIModels />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/models/:provider"
                    element={
                      <AiRouteShell>
                        <ModelProviderRoute />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/knowledge-base"
                    element={
                      <AiRouteShell>
                        <KnowledgeBase />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/knowledge-base/:folder"
                    element={
                      <AiRouteShell>
                        <KnowledgeBaseFolderRoute />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/tools"
                    element={
                      <AiRouteShell>
                        <Tools />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/agents"
                    element={
                      <AiRouteShell>
                        <Agents />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/agent-details/:id"
                    element={
                      <AiRouteShell>
                        <AgentDetailsPage />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/agent-details/:id/:tab"
                    element={
                      <AiRouteShell>
                        <AgentDetailsPage />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/tool-details/:id/authentication"
                    element={
                      <AiRouteShell>
                        <ToolDetailsRoute tab="authentication" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/tool-details/:id/api"
                    element={
                      <AiRouteShell>
                        <ToolDetailsRoute tab="api" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/tool-details/:id/dashboard"
                    element={
                      <AiRouteShell>
                        <ToolDetailsRoute tab="dashboard" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/tool-details/:id/tutorial-guide"
                    element={
                      <AiRouteShell>
                        <ToolDetailsRoute tab="tutorial-guide" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/mcp-server-details/:id/configuration"
                    element={
                      <AiRouteShell>
                        <McpServerDetailsRoute tab="configuration" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/mcp-server-details/:id/dashboard"
                    element={
                      <AiRouteShell>
                        <McpServerDetailsRoute tab="dashboard" />
                      </AiRouteShell>
                    }
                  />
                  <Route
                    path="/ai/mcp-server-details/:id/tutorial-guide"
                    element={
                      <AiRouteShell>
                        <McpServerDetailsRoute tab="tutorial-guide" />
                      </AiRouteShell>
                    }
                  />
                </Route>

                <Route path="/notes" element={<NotesPage />} />
                <Route path="/notes/create" element={<CreateNotePage />} />
                <Route path="/notes/:noteId" element={<EditNotePage />} />

                <Route path="/503" element={<ServiceUnavailablePage />} />
                <Route path="/404" element={<NotFoundPage />} />
              </Route>

              {/* Full-page routes (no sidebar) */}
              <Route
                element={
                  <Guard>
                    <FullPageLayout />
                  </Guard>
                }
              >
                <Route
                  path="/create-api/:toolId"
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <CreateAPIRoute />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/chat" />} />

              <Route path="*" element={<Navigate to="/404" />} />
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
};
